import React, { useState, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Text,
  Table,
  Box,
  Flex,
  useColorModeValue,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';
import { SortableTh } from 'components/table';
import type { SortState } from 'components/table';

import { apiUnassignedLeadList } from 'api';
import { useViewAs } from 'hooks/useViewAs';

import type {
  APIPaginatedResponse,
  APILeadsListResultData,
} from 'types/api.types';

import getDateAge from 'helpers/getDateAge';

import UnassignedListActions from 'views/leads/components/UnassignedListActions';

const TABLE_HEADERS: { label: string; field?: string }[] = [
  { label: 'Client name', field: 'client_name' },
  { label: 'Type', field: 'type' },
  { label: 'Workflow', field: 'workflow' },
  { label: 'Source', field: 'source' },
  { label: 'Age', field: 'age' },
  { label: 'Actions' },
];

const INITIAL_VISIBLE_ROWS = 5;

const UnassignedLeadList = ({
  searchQuery,
  sortQuery,
  sort,
  setLeadCount,
}: {
  searchQuery: string;
  sortQuery: string;
  sort: SortState;
  setLeadCount: (count: number) => void;
}) => {
  const [page, setPage] = useState(0);
  const [showAllRows, setShowAllRows] = useState(false);
  const viewAs = useViewAs();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBgColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const buttonBgColor = useColorModeValue('white', 'navy.800');
  const buttonHoverBgColor = useColorModeValue('gray.50', 'whiteAlpha.100');

  const query = [searchQuery, sortQuery].filter(Boolean).join('&');

  const { error, data, isLoading } = useQuery({
    queryKey: ['unassigned-lead-list', page, query, viewAs.filterParams],
    queryFn: () =>
      apiUnassignedLeadList(
        page + 1,
        query,
        viewAs.viewAsEmail,
        viewAs.viewAsBranchId,
      ),
    refetchInterval: 120000, // 2 minutes
  }) as {
    data: APIPaginatedResponse<APILeadsListResultData>;
    isLoading: boolean;
    error: any;
    refetch: () => Promise<any>;
    isFetching: boolean;
  };

  useEffect(() => {
    if (searchQuery) {
      setPage(0);
      setShowAllRows(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [sortQuery]);

  useEffect(() => {
    if (!data) return;
    setLeadCount(data.count);
  }, [data, setLeadCount]);

  const handleToggleShowAll = () => {
    setShowAllRows(!showAllRows);

    if (!showAllRows) {
      setPage(0);
    }
  };

  if (isLoading || !data || !data?.results) {
    return (
      <Box textAlign="center" mt="20px">
        Loading unassigned leads...
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="20px" pb="0">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  const hasMoreThanInitialRows = data.count > INITIAL_VISIBLE_ROWS;
  const displayedResults = showAllRows
    ? data.results
    : data.results.slice(0, INITIAL_VISIBLE_ROWS);

  return (
    <>
      <Table variant="simple" color="gray.500" mt="20px">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map(({ label, field }, index) => {
              const thProps = {
                borderColor,
                w: '20%',
                borderTop: '1px solid',
                borderTopColor: borderColor,
              };

              return field ? (
                <SortableTh
                  key={label}
                  label={label}
                  field={field}
                  {...sort}
                  {...thProps}
                />
              ) : (
                <Th key={label} pe="10px" {...thProps}>
                  <Box
                    color="gray.400"
                    textAlign={
                      index === TABLE_HEADERS.length - 1 ? 'center' : 'left'
                    }
                  >
                    {label}
                  </Box>
                </Th>
              );
            })}
          </Tr>
        </Thead>

        <Tbody>
          {displayedResults.map((lead) => (
            <Tr
              key={lead.id}
              _hover={{
                bg: hoverBgColor,
              }}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {lead.first_name} {lead.last_name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {lead.type_display}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {lead.workflow?.name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {lead.source?.name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text
                    color={textColor}
                    fontSize="sm"
                    fontWeight="700"
                    whiteSpace="nowrap"
                  >
                    {getDateAge(new Date(lead.created_at))}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <UnassignedListActions lead={lead} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {displayedResults.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No leads found
        </Box>
      )}

      {hasMoreThanInitialRows && (
        <Flex justifyContent="center" mt="3">
          <Button
            onClick={handleToggleShowAll}
            size="sm"
            variant="outline"
            bg={buttonBgColor}
            _hover={{ bg: buttonHoverBgColor }}
            rightIcon={showAllRows ? <ChevronUpIcon /> : <ChevronDownIcon />}
          >
            {showAllRows ? 'Show Less' : `Show All (${data.count})`}
          </Button>
        </Flex>
      )}

      {showAllRows && (
        <Paginate page={page} count={data.count} onSetPage={setPage} />
      )}
    </>
  );
};

export default React.memo(UnassignedLeadList);
